import React from 'react';
import { MultiplePagesEmpty, RefreshDouble, WarningCircle } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import {
  useDeclarations,
  useDeclarationDocuments,
  downloadDeclarationDocument,
  type DeclarationItem,
  type DeclarationDocument,
} from '../../features/settings/hooks';

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation('settings');

  if (status === 'presented') {
    return (
      <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
        {t('declarations.status.presented')}
      </span>
    );
  }
  if (status === 'declaration_in_progress') {
    return (
      <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
        {t('declarations.status.declaration_in_progress')}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-700">
      {t('declarations.status.in_progress')}
    </span>
  );
}

function DeclarationRow({ declaration }: { declaration: DeclarationItem }) {
  const { t } = useTranslation(['settings', 'common']);
  const [expanded, setExpanded] = React.useState(false);
  const [downloading, setDownloading] = React.useState<string | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const canDownload = declaration.status === 'presented';

  const { data: docs, isLoading: docsLoading, refetch } = useDeclarationDocuments(
    declaration.session_uuid,
    canDownload,
  );

  async function handleDownload(doc: DeclarationDocument) {
    setDownloading(doc.id);
    setActionError(null);
    try {
      await downloadDeclarationDocument(doc.id, doc.filename);
    } catch {
      setActionError(t('settings:declarations.download_failed'));
    } finally {
      setDownloading(null);
    }
  }

  async function handleDownloadClick() {
    setActionError(null);
    let docList = docs;
    if (!docList) {
      const result = await refetch();
      docList = result.data;
    }
    if (!docList || docList.length === 0) {
      setActionError(t('settings:declarations.no_document'));
      return;
    }
    if (docList.length === 1) {
      await handleDownload(docList[0]);
      return;
    }
    setExpanded(true);
  }

  const displayDate = declaration.completed_at ?? declaration.started_at;
  const isBusy = docsLoading || !!downloading;

  function renderDownloadCell() {
    if (!canDownload) return null;

    if (expanded && docs && docs.length >= 2) {
      return null;
    }

    return (
      <button
        onClick={() => void handleDownloadClick()}
        disabled={isBusy}
        className="text-xs text-[#1066CB] hover:underline disabled:text-gray-400 disabled:cursor-not-allowed"
      >
        {downloading
          ? t('settings:declarations.downloading')
          : docsLoading
            ? t('settings:declarations.downloading')
            : t('settings:declarations.download_document')}
      </button>
    );
  }

  return (
    <>
      <tr>
        <td className="px-4 py-3 text-sm text-gray-900 border-r border-[#e2e8f0]">
          {declaration.tax_year ?? '—'}
        </td>
        <td className="px-4 py-3 text-sm text-gray-600 border-r border-[#e2e8f0]">
          {formatDate(displayDate)}
        </td>
        <td className="px-4 py-3 border-r border-[#e2e8f0]">
          <StatusBadge status={declaration.status} />
        </td>
        <td className="px-4 py-3 text-sm">
          {renderDownloadCell()}
          {actionError && <p className="text-xs text-red-600 mt-1">{actionError}</p>}
        </td>
      </tr>
      {docs && docs.length >= 2 && expanded && (
        <tr className="bg-gray-50">
          <td colSpan={4} className="px-4 py-2">
            <ul className="space-y-1">
              {docs.map((doc) => (
                <li key={doc.id}>
                  <button
                    onClick={() => handleDownload(doc)}
                    disabled={downloading === doc.id}
                    className="text-xs text-[#1066CB] hover:underline disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    {downloading === doc.id
                      ? t('settings:declarations.downloading')
                      : doc.filename}
                  </button>
                </li>
              ))}
            </ul>
          </td>
        </tr>
      )}
    </>
  );
}

export default function TaxDeclarationsTab() {
  const { t } = useTranslation(['settings', 'common']);
  const { data, isLoading, isError, refetch } = useDeclarations();

  if (isLoading) {
    return <p className="text-sm text-gray-500 py-4">{t('common:loading.generic')}</p>;
  }
  if (isError) {
    return (
      <div className="flex flex-col items-center gap-3 py-12 text-center">
        <WarningCircle className="h-8 w-8 text-gray-400" />
        <p className="text-sm font-medium text-gray-700">{t('settings:declarations.load_error')}</p>
        <p className="text-xs text-gray-500">{t('settings:shared.load_error_hint')}</p>
        <button
          type="button"
          onClick={() => refetch()}
          className="mt-1 inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <RefreshDouble className="h-4 w-4" />
          {t('common:buttons.try_again')}
        </button>
      </div>
    );
  }
  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-16 text-center">
        <div className="rounded-full bg-gray-100 p-4">
          <MultiplePagesEmpty className="h-7 w-7 text-gray-400" />
        </div>
        <p className="text-sm font-medium text-gray-700">{t('settings:declarations.empty_title')}</p>
        <p className="text-xs text-gray-500 max-w-xs">{t('settings:declarations.empty_body')}</p>
        <a
          href="/new"
          className="mt-2 inline-flex items-center rounded-lg bg-[#1066CB] px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
        >
          {t('settings:declarations.start_return')}
        </a>
      </div>
    );
  }

  return (
    <div className="border border-[#e2e8f0] rounded-lg overflow-hidden">
      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-[#e2e8f0] bg-[#f9fafb]">
            <th className="px-4 py-3 text-xs font-semibold text-gray-500 border-r border-[#e2e8f0]">
              {t('settings:declarations.table.year')}
            </th>
            <th className="px-4 py-3 text-xs font-semibold text-gray-500 border-r border-[#e2e8f0]">
              {t('settings:declarations.table.date')}
            </th>
            <th className="px-4 py-3 text-xs font-semibold text-gray-500 border-r border-[#e2e8f0]">
              {t('settings:declarations.table.type')}
            </th>
            <th className="px-4 py-3 text-xs font-semibold text-gray-500"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#e2e8f0]">
          {data.map((d) => (
            <DeclarationRow key={d.session_uuid} declaration={d} />
          ))}
        </tbody>
      </table>
    </div>
  );
}
